import { InstabookAvailability, InstabookAvailabilityApiResponse } from 'src/common/types/api/v4/instabook';
import { apiRequest } from 'src/common/utils/apiFetch';
import captureException from 'src/common/utils/captureException';
import retry from 'src/common/utils/retry';

type InstabookParams = {
  listingId: string;
  /** date format: `YYYY-MM-DD` */
  startDate: string;
  /** date format: `YYYY-MM-DD` */
  endDate: string;
  capacity?: string;
  /** currency should be currency code. For example: `USD` */
  currency?: string;
  /** locale should be locale code. For example: `en` */
  locale?: string;
  /** Pricing is different for direct instabooks  */
  isDirectBooking?: boolean;
};

const RETRY_ATTEMPTS = 3;

const getDefaultResponse = (): InstabookAvailabilityApiResponse => ({
  count: 0,
  results: [],
  next: null,
  previous: null,
});

const getInstabookPage = async (
  searchParams: URLSearchParams,
): Promise<InstabookAvailabilityApiResponse> => {
  if (typeof window === 'undefined') {
    return getDefaultResponse();
  }
  const url = `/api/v4/instabook/availability/?${searchParams.toString()}`;
  let latestError: string | null = null;
  const response = await retry(
    RETRY_ATTEMPTS,
    async () => {
      const r = await apiRequest(url);
      if (r.status === 200) {
        const data: InstabookAvailabilityApiResponse = await r.json();
        return data;
      }
      latestError = r.statusText;
      return null;
    },
    async () => {
      captureException(
        new Error(`Failed to retrieve instabooks: ${latestError}`),
      );
      return null;
    },
  );
  return response ?? getDefaultResponse();
};

const PAGE_SIZE = 25;

/**
 * Pagination can take time, especially for large Instabook page counts.
 * Instead of paging being handled internally here, it is controlled by the call-site.
 * Every page is yielded back, and the call-site can decide whether to continue or not
 * i.e. the paging can be aborted.
 *
 * The yielded values are empty until the final return, only because there is no
 * current requirement for partial values.
 */
async function* getPaginatedInstabooks({
  listingId,
  startDate,
  endDate,
  capacity,
  currency,
  locale,
  isDirectBooking,
}: InstabookParams): AsyncGenerator<InstabookAvailability[]> {
  let params = new URLSearchParams({
    boat_id: listingId,
    start_date: startDate,
    end_date: endDate,
    page_size: `${PAGE_SIZE}`,
  });

  if (capacity) {
    params.append('guest_count', capacity);
  }

  if (currency) {
    params.append('currency', currency);
  }

  if (locale) {
    params.append('lang', locale);
  }

  if (isDirectBooking) {
    params.append('is_direct_booking', 'true');
  }

  const instabookTrips: InstabookAvailability[] = [];

  // exits if `next` is empty in response
  for (;;) {
    // TODO: parallelize from call 2 onwards
    // eslint-disable-next-line no-await-in-loop
    const { results, next } = await getInstabookPage(params);
    if (!results) return [];

    instabookTrips.push(...results);

    const nextParams = next?.split('?')[1];
    if (nextParams) {
      // Paging is controlled externally so it can be cancelled
      yield [];
      params = new URLSearchParams(nextParams);
    } else {
      break;
    }
  }
  return instabookTrips;
}

export default getPaginatedInstabooks;
